import { FC } from 'react';
import { array, nonEmptyArray, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { Ord } from 'fp-ts/string';
import { FormCounterField, LocalizedProperty } from '@mews/framework';
import { Highlight, Stack, StackItem } from '@optimus-web/core';
import { OrderItem } from '@mews-commander/data-access';
import { sumBy } from '@mews/fp';
import { getOrderItemMultiplier, isOrderItemClaimed } from '@mews-commander/legacy-entities/src/ducks/orderItemNew';
import { sortByAgeCategoryOrd } from '@mews-commander/legacy-entities/src/ducks/ageCategories';
import { isDefaultAgeCategoryForService } from '@mews-commander/legacy-entities/src/ducks/ageCategoriesNew';
import { ProductCheckListDataRow } from '../../../types';
import { useAgeCategoriesContext } from '../../../contexts/AgeCategoriesContext';
import { getAgeCategoryLabel, getOrderItemAgeCategoryIdentifier } from '../../../utils/ageCategories';
import { COUNTER_NAME_CONCAT_CHARACTER, ClaimPerPersonPanelDataTestIds, MAX_COUNTER_WIDTH } from './constants';

type ClaimPerPersonFormFieldsProps = ProductCheckListDataRow;

export const ClaimPerPersonFormFields: FC<ClaimPerPersonFormFieldsProps> = ({
    productsData,
    productServiceOrder,
    serviceId,
}) => (
    <>
        {productsData.map(({ data, orderItems }) => (
            <Stack vertical key={data.id} spacing="100">
                <Highlight size="medium">
                    <LocalizedProperty property={data.names} />
                </Highlight>
                <ProductCounters
                    orderItems={orderItems}
                    productId={data.id}
                    isProductServiceOrder={!!productServiceOrder}
                    serviceId={serviceId}
                />
            </Stack>
        ))}
    </>
);

const ProductCounters: React.FC<{
    orderItems: OrderItem[];
    productId: string;
    serviceId: string | undefined;
    isProductServiceOrder: boolean;
}> = ({ orderItems, productId, isProductServiceOrder, serviceId }) => {
    const { ageCategoriesById, sortedAgeCategories } = useAgeCategoriesContext();

    const defaultAgeCategory = sortedAgeCategories.find(isDefaultAgeCategoryForService(serviceId));

    const orderItemsByAgeCategories = groupByAgeCategory(orderItems, isProductServiceOrder, defaultAgeCategory?.id);

    return (
        <Stack spacing="100" inline wrap distribution="space-between">
            {pipe(
                orderItemsByAgeCategories,
                record.collect(Ord)((ageCategoryId, items) => ({ ageCategoryId, items })),
                array.sort(sortByAgeCategoryOrd(sortedAgeCategories)),
                array.map(({ ageCategoryId: ageCategoryIdentifier, items }) => {
                    const category = ageCategoriesById[ageCategoryIdentifier];
                    const claimedProducts = items.filter(isOrderItemClaimed).length;
                    const max = sumBy(getOrderItemMultiplier, items);
                    const name = `${productId}${COUNTER_NAME_CONCAT_CHARACTER}${ageCategoryIdentifier}`;
                    return (
                        <StackItem width={MAX_COUNTER_WIDTH} key={name}>
                            <FormCounterField
                                name={name}
                                size="medium"
                                label={getAgeCategoryLabel(category)}
                                defaultValue={claimedProducts}
                                min={0}
                                max={max}
                                data-test-id={ClaimPerPersonPanelDataTestIds.counter}
                                data-age-category-id={ageCategoryIdentifier}
                                data-product-id={productId}
                            />
                        </StackItem>
                    );
                })
            )}
        </Stack>
    );
};

const groupByAgeCategory = (orderItems: OrderItem[], isProductServiceOrder: boolean, defaultAgeCategoryId?: string) =>
    pipe(
        orderItems,
        nonEmptyArray.groupBy((orderItem) =>
            getOrderItemAgeCategoryIdentifier(orderItem, isProductServiceOrder, defaultAgeCategoryId)
        )
    );
